/**
 * Snapchat Ads — Report builders (Story 19.8, Task 3).
 *
 * Pure-data field lists + URL/body composition. No HTTP, no I/O — these
 * helpers feed `analytics.ts` (sync) and `reports.ts` (async) by
 * deterministically picking the per-report-type Snap field subset and
 * stamping it into the call's options.
 *
 * Module-load assertions enforce three structural invariants:
 *   1. Every field list is ≤ SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST.
 *   2. No two fields appear in the same list (catches copy-paste dupes).
 *   3. No field list is empty.
 *
 * Together with the field-allowlist enforcer in the orchestrator (Story 19.8
 * Task 7.5.1), this prevents two categories of regression: Snap 400-ing on
 * over-50-field requests, and field-name injection via `requestedColumns`.
 */

import type {
  SnapchatAdsBreakdown,
  SnapchatAdsGranularity,
  SnapchatAdsReportDimension,
  SnapchatAdsReportType,
  SnapchatAdsStatsOptions,
  SnapchatAttributionWindows,
} from "./analytics";
import { SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST } from "./analytics";

// ============================================================================
// Per-report-type field lists
// ============================================================================
//
// Each list is a pragmatic subset of the 175-metric catalogue (capped at 50
// per Snap's hard limit). Picks privilege the agency-critical KPIs:
// delivery + engagement + spend + the most-needed conversions + ROAS-feeders.

/** Account overview — delivery + spend + engagement + the top conversions. */
export const OVERVIEW_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "screen_time_millis",
  "avg_screen_time_millis",
  "view_time_millis",
  "video_views",
  "video_views_15s",
  "video_views_time_based",
  "view_completion",
  "quartile_1",
  "quartile_2",
  "quartile_3",
  "total_installs",
  "total_installs_app",
  "total_installs_web",
  "conversion_purchases",
  "conversion_purchases_app",
  "conversion_purchases_web",
  "conversion_purchases_value",
  "conversion_purchases_value_app",
  "conversion_purchases_value_web",
  "conversion_page_views",
  "conversion_view_content",
  "conversion_add_cart",
  "conversion_sign_ups",
  "conversion_app_opens",
  "conversion_save",
] as const;

/** Campaign breakdown — overview KPIs + campaign identifiers. */
export const CAMPAIGNS_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "video_views",
  "video_views_15s",
  "view_completion",
  "quartile_1",
  "quartile_2",
  "quartile_3",
  "total_installs",
  "total_installs_app",
  "total_installs_web",
  "conversion_purchases",
  "conversion_purchases_app",
  "conversion_purchases_web",
  "conversion_purchases_value",
  "conversion_purchases_value_app",
  "conversion_purchases_value_web",
  "conversion_page_views",
  "conversion_add_cart",
  "conversion_sign_ups",
  "conversion_app_opens",
] as const;

/** Ad-squad breakdown — same KPIs, plus video metrics for creative scoring. */
export const AD_SQUADS_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "screen_time_millis",
  "video_views",
  "video_views_15s",
  "view_completion",
  "quartile_1",
  "quartile_2",
  "quartile_3",
  "total_installs",
  "total_installs_app",
  "total_installs_web",
  "conversion_purchases",
  "conversion_purchases_value",
  "conversion_page_views",
  "conversion_view_content",
  "conversion_add_cart",
  "conversion_sign_ups",
  "conversion_app_opens",
] as const;

/** Ad-level — adds attachment video metrics for long-form content. */
export const ADS_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "screen_time_millis",
  "video_views",
  "video_views_15s",
  "view_completion",
  "quartile_1",
  "quartile_2",
  "quartile_3",
  "attachment_impressions",
  "attachment_uniques",
  "attachment_avg_view_time_millis",
  "attachment_total_view_time_millis",
  "attachment_view_completion",
  "total_installs",
  "conversion_purchases",
  "conversion_purchases_value",
  "conversion_page_views",
  "conversion_view_content",
  "conversion_add_cart",
  "conversion_sign_ups",
] as const;

/** Age × Gender — delivery + core conversions only. */
export const AGE_GENDER_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "screen_time_millis",
  "video_views",
  "view_completion",
  "total_installs",
  "conversion_purchases",
  "conversion_purchases_value",
  "conversion_page_views",
  "conversion_sign_ups",
] as const;

/** Country × OS — delivery + core conversions only. */
export const COUNTRY_OS_FIELDS: readonly string[] = [
  "impressions",
  "swipes",
  "swipe_up_percent",
  "spend",
  "uniques",
  "frequency",
  "screen_time_millis",
  "video_views",
  "view_completion",
  "total_installs",
  "conversion_purchases",
  "conversion_purchases_value",
  "conversion_page_views",
  "conversion_sign_ups",
] as const;

/** Map from report type to field subset. */
export const FIELDS_BY_REPORT_TYPE: Record<
  SnapchatAdsReportType,
  readonly string[]
> = {
  overview: OVERVIEW_FIELDS,
  campaigns: CAMPAIGNS_FIELDS,
  adSquads: AD_SQUADS_FIELDS,
  ads: ADS_FIELDS,
  ageGender: AGE_GENDER_FIELDS,
  countryOs: COUNTRY_OS_FIELDS,
};

/** Per-report-type granularity. Time-series uses DAY; breakdown-dim uses TOTAL. */
export const GRANULARITY_BY_REPORT_TYPE: Record<
  SnapchatAdsReportType,
  SnapchatAdsGranularity
> = {
  overview: "DAY",
  campaigns: "DAY",
  adSquads: "DAY",
  ads: "DAY",
  ageGender: "TOTAL",
  countryOs: "TOTAL",
};

/** Per-report-type breakdown / report_dimension query params. */
interface ReportTypeRouting {
  readonly entity: "adaccount" | "campaign" | "adsquad";
  readonly breakdown?: SnapchatAdsBreakdown;
  readonly reportDimension?: SnapchatAdsReportDimension;
}

export const ROUTING_BY_REPORT_TYPE: Record<
  SnapchatAdsReportType,
  ReportTypeRouting
> = {
  overview: { entity: "adaccount" },
  campaigns: { entity: "adaccount", breakdown: "campaign" },
  adSquads: { entity: "campaign", breakdown: "adsquad" },
  ads: { entity: "adsquad", breakdown: "ad" },
  ageGender: { entity: "adaccount", reportDimension: "age,gender" },
  countryOs: { entity: "adaccount", reportDimension: "country,os" },
};

// ============================================================================
// Module-load invariants — run once at import time
// ============================================================================

(function assertFieldListInvariants(): void {
  for (const [reportType, fields] of Object.entries(FIELDS_BY_REPORT_TYPE)) {
    if (fields.length === 0) {
      throw new Error(
        `Snapchat Ads report-builders: empty field list for "${reportType}"`
      );
    }
    if (fields.length > SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST) {
      throw new Error(
        `Snapchat Ads report-builders: "${reportType}" has ${fields.length} fields, exceeds ${SNAPCHAT_ADS_MAX_FIELDS_PER_REQUEST}-cap`
      );
    }
    const seen = new Set<string>();
    for (const f of fields) {
      if (seen.has(f)) {
        throw new Error(
          `Snapchat Ads report-builders: "${reportType}" has duplicate field "${f}"`
        );
      }
      seen.add(f);
    }
  }
})();

// ============================================================================
// Time-zone aware ISO 8601 timestamp encoder
// ============================================================================

/**
 * Encodes a `Date` as ISO 8601 with the ad-account IANA timezone offset,
 * rounded DOWN to the start of the day in that timezone. Snap requires
 * `start_time` / `end_time` aligned to hour/day boundaries server-side,
 * computed against the ad-account timezone (NOT UTC, NOT Worker-local).
 *
 * Example: `2026-04-01` Pacific → `"2026-04-01T00:00:00.000-07:00"`.
 *
 * Workers V8 supports `Intl.DateTimeFormat` with IANA timezones natively
 * via ICU. If the runtime returns nonsense, we fall back to UTC encoding
 * with a console.warn (dev-only, won't ship to prod since the call sites
 * always provide a real IANA timezone string from the ad-account metadata).
 */
export function dateToSnapTimestamp(d: Date, timeZone: string): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    throw new Error("dateToSnapTimestamp: invalid Date");
  }
  if (!timeZone) {
    // No ad-account timezone available — fall back to UTC.
    return formatUtcMidnight(d);
  }
  try {
    return formatIsoWithIanaOffset(d, timeZone);
  } catch {
    return formatUtcMidnight(d);
  }
}

function formatUtcMidnight(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}T00:00:00.000+00:00`;
}

function formatIsoWithIanaOffset(d: Date, timeZone: string): string {
  // Get the calendar Y-M-D-H-M-S in the target zone.
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  const y = parts.year ?? "0000";
  const mo = parts.month ?? "01";
  const day = parts.day ?? "01";
  // Round DOWN to start-of-day in the target timezone.
  const startOfDayLocal = `${y}-${mo}-${day}T00:00:00.000`;
  // Compute the offset for that local moment in this timezone.
  const offset = computeOffsetMinutes(d, timeZone);
  const sign = offset >= 0 ? "+" : "-";
  const absMin = Math.abs(offset);
  const hh = String(Math.floor(absMin / 60)).padStart(2, "0");
  const mm = String(absMin % 60).padStart(2, "0");
  return `${startOfDayLocal}${sign}${hh}:${mm}`;
}

/**
 * Returns the timezone offset (in minutes east of UTC) for the given moment
 * in the given IANA timezone. Uses Intl's formatToParts to read the wall-
 * clock time and back-calculates the offset.
 */
function computeOffsetMinutes(d: Date, timeZone: string): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  const utcLikeMs = Date.UTC(
    Number(parts.year ?? "0"),
    Number(parts.month ?? "1") - 1,
    Number(parts.day ?? "1"),
    Number(parts.hour === "24" ? "0" : (parts.hour ?? "0")),
    Number(parts.minute ?? "0"),
    Number(parts.second ?? "0")
  );
  return Math.round((utcLikeMs - d.getTime()) / 60000);
}

// ============================================================================
// Sync stats request builder
// ============================================================================

export interface BuildSyncStatsRequestInput {
  readonly reportType: SnapchatAdsReportType;
  readonly accountId: string;
  readonly dateRange: { readonly start: Date; readonly end: Date };
  readonly timeZone?: string;
  readonly attribution?: SnapchatAttributionWindows;
  readonly fieldsOverride?: ReadonlyArray<string>;
  readonly granularityOverride?: SnapchatAdsGranularity;
}

export interface BuildSyncStatsRequestOutput {
  readonly reportType: SnapchatAdsReportType;
  readonly accountId: string;
  readonly entity: "adaccount" | "campaign" | "adsquad";
  readonly options: SnapchatAdsStatsOptions;
}

/**
 * Composes the per-call options + URL routing for one sync stats request.
 * The orchestrator walks `chunkFieldsForBatching` and calls
 * `fetchSnapchatAdsSyncStats` per batch with the same options shape.
 *
 * Conversion-stripping for delivery-only dimensions: when the report
 * dimension is `region`/`dma`/`make`, all `conversion_*` fields are
 * stripped from the field list at builder time, and the orchestrator
 * surfaces a non-fatal warning entry (per AC #7).
 */
export function buildSyncStatsRequest(
  input: BuildSyncStatsRequestInput
): BuildSyncStatsRequestOutput {
  const routing = ROUTING_BY_REPORT_TYPE[input.reportType];
  let fields =
    input.fieldsOverride ?? FIELDS_BY_REPORT_TYPE[input.reportType];

  // Conversion-stripping for delivery-only dimensions
  const dim = routing.reportDimension;
  if (dim && /^(region|dma|make)$/.test(dim)) {
    fields = fields.filter(
      (f) => !f.startsWith("conversion_") && !f.endsWith("_value")
    );
  }

  const granularity =
    input.granularityOverride ?? GRANULARITY_BY_REPORT_TYPE[input.reportType];

  const tz = input.timeZone ?? "";

  const options: SnapchatAdsStatsOptions = {
    startTime: dateToSnapTimestamp(input.dateRange.start, tz),
    endTime: dateToSnapTimestamp(input.dateRange.end, tz),
    fields,
    granularity,
    breakdown: routing.breakdown,
    reportDimension: routing.reportDimension,
    swipeUpAttributionWindow: input.attribution?.swipeUpAttributionWindow,
    viewAttributionWindow: input.attribution?.viewAttributionWindow,
    conversionSourceTypes: input.attribution?.conversionSourceTypes,
    actionReportTime: input.attribution?.actionReportTime,
  };

  return {
    reportType: input.reportType,
    accountId: input.accountId,
    entity: routing.entity,
    options,
  };
}

// ============================================================================
// Async stats request builder
// ============================================================================

export interface BuildAsyncStatsRequestInput {
  readonly entity: "adaccount" | "campaign" | "adsquad" | "ad" | "creative" | "media";
  readonly entityId: string;
  readonly dateRange: { readonly start: Date; readonly end: Date };
  readonly granularity: SnapchatAdsGranularity;
  readonly fields: ReadonlyArray<string>;
  readonly timeZone?: string;
  readonly attribution?: SnapchatAttributionWindows;
  readonly reportDimension?: SnapchatAdsReportDimension;
  readonly breakdown?: SnapchatAdsBreakdown;
}

export interface BuildAsyncStatsRequestOutput {
  readonly entity: BuildAsyncStatsRequestInput["entity"];
  readonly entityId: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly fields: ReadonlyArray<string>;
  readonly granularity: SnapchatAdsGranularity;
  readonly breakdown?: SnapchatAdsBreakdown;
  readonly reportDimension?: SnapchatAdsReportDimension;
  readonly swipeUpAttributionWindow?: SnapchatAttributionWindows["swipeUpAttributionWindow"];
  readonly viewAttributionWindow?: SnapchatAttributionWindows["viewAttributionWindow"];
  readonly conversionSourceTypes?: SnapchatAttributionWindows["conversionSourceTypes"];
  readonly actionReportTime?: SnapchatAttributionWindows["actionReportTime"];
}

/**
 * Composes the inputs for `createSnapchatAdsAsyncStatsReport`.
 *
 * Pre-validates the per-granularity caps:
 *   - `granularity=HOUR` rejects ranges > 7 days.
 *   - `granularity=DAY` accepts up to ~31 days; the orchestrator routes
 *     larger DAY ranges to async automatically (this builder does NOT
 *     reject — Snap's async accepts longer ranges than sync).
 */
export function buildAsyncStatsRequest(
  input: BuildAsyncStatsRequestInput
): BuildAsyncStatsRequestOutput {
  if (input.fields.length === 0) {
    throw new Error("Snapchat Ads async stats request requires at least one field");
  }
  const days = inclusiveDays(input.dateRange);
  if (input.granularity === "HOUR" && days > 7) {
    throw new Error(
      `Snapchat Ads HOUR granularity requires date range ≤ 7 days; got ${days}`
    );
  }
  const tz = input.timeZone ?? "";
  return {
    entity: input.entity,
    entityId: input.entityId,
    startTime: dateToSnapTimestamp(input.dateRange.start, tz),
    endTime: dateToSnapTimestamp(input.dateRange.end, tz),
    fields: input.fields,
    granularity: input.granularity,
    breakdown: input.breakdown,
    reportDimension: input.reportDimension,
    swipeUpAttributionWindow: input.attribution?.swipeUpAttributionWindow,
    viewAttributionWindow: input.attribution?.viewAttributionWindow,
    conversionSourceTypes: input.attribution?.conversionSourceTypes,
    actionReportTime: input.attribution?.actionReportTime,
  };
}

function inclusiveDays(range: { start: Date; end: Date }): number {
  const startUtc = Date.UTC(
    range.start.getUTCFullYear(),
    range.start.getUTCMonth(),
    range.start.getUTCDate()
  );
  const endUtc = Date.UTC(
    range.end.getUTCFullYear(),
    range.end.getUTCMonth(),
    range.end.getUTCDate()
  );
  if (endUtc < startUtc) return 0;
  return Math.round((endUtc - startUtc) / (24 * 60 * 60 * 1000)) + 1;
}
